import {
  RADAR_AUTOCOMPLETE_API_URL,
  RADAR_DEFAULT_COUNTRY,
  RADAR_DEFAULT_LIMIT,
  RADAR_LAYERS,
} from '../config';

import type {
  FetchPredictions,
  FetchPredictionsParams,
  PredictionsResponse,
} from './fetch-predictions.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches autocomplete predictions using Radar Autocomplete API.
 *
 * @see {@link https://radar.com/documentation/api#autocomplete}
 */
export const fetchPredictions: FetchPredictions = async (props: FetchPredictionsParams) => {
  const {
    query,
    apiKey,
    near,
    layers = [RADAR_LAYERS.ADDRESS],
    limit = RADAR_DEFAULT_LIMIT,
    country = RADAR_DEFAULT_COUNTRY,
  } = props;

  const apiUrl = generateAutocompleteApiUrl({
    query,
    near,
    layers,
    limit,
    country,
  });

  const apiResponse = await fetch(apiUrl, {
    headers: { Authorization: apiKey },
  });

  if (!apiResponse.ok) {
    throw new Error(`Radar autocomplete request failed with status ${apiResponse.status}`);
  }

  const data: PredictionsResponse | undefined = await apiResponse.json();

  return data ?? { addresses: [] };
};

/**
 * Generates Radar API URL based on provided params.
 */
function generateAutocompleteApiUrl(props: GenerateUrlProps): string {
  const { query, near, layers, limit, country } = props;

  const url = new URL(RADAR_AUTOCOMPLETE_API_URL);

  url.searchParams.set('query', query);
  url.searchParams.set('layers', layers.join(','));
  url.searchParams.set('limit', String(limit));
  url.searchParams.set('country', country);

  if (near) {
    url.searchParams.set('near', near);
  }

  return url.toString();
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

type GenerateUrlProps = Readonly<{
  query: string;
  near: string | undefined;
  layers: readonly string[];
  limit: number;
  country: string;
}>;
